
/**
 * @file Skills.jsx
 * @description Skills component for displaying and managing skills.
 * @component Skills
 * @exports Skills - Main component for displaying and managing skills.
 */

import React, { useState, useEffect, useMemo, use } from "react";
import Grid from "@mui/material/Grid2";
import { Add } from "@mui/icons-material";
import { TextField, Button, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getIvrQueue } from "../../../services/ivrModule/queueServices.js";
import { getUserSkills, editUserSkill, addUserSkill } from "../../../services/adminModule/skills/skillServices.js";
import { QUEUE_ERRORS } from "../../../common/constants/ivrModule/queueConstants.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { SKILL_STRINGS, SKILL_KEYS, SKILL_MESSAGES, SKILL_ERRORS, SKILL_TABLE_COLUMNS } from "../../../common/constants/adminModule/skillConstants.js";
import SkillsTable from "./SkillsTable.jsx";
import AddEditSkillDialog from "./AddEditSkillDialog.jsx";

const Skills = () => {
    const [page, setPage] = useState(0);
    const [ivrQueues, setIvrQueues] = useState([]);
    const [userSkills, setUserSkills] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loadingQueues, setLoadingQueues] = useState(true);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [loadingSkills, setLoadingSkills] = useState(true);
    const [searchUserSkill, setSearchUserSkill] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [orderBy, setOrderBy] = useState(SKILL_KEYS.SKILL_NAME);
    const [isAddEditSkillModalOpen, setIsAddEditSkillModalOpen] = useState(false);

    useEffect(() => { 
        fetchSkills();
        fetchQueues();
    }, []);

    const fetchSkills = async () => {
        try {
            setLoadingSkills(true);
            setUserSkills(await getUserSkills());
        }
        catch (error) {
            console.error(SKILL_ERRORS.FETCH_SKILLS_ERROR, error);
        }
        finally {
            setLoadingSkills(false);
        }
    };
    
    const fetchQueues = async () => {
        try {
            setLoadingQueues(true);
            const queues = await getIvrQueue();
            const queueIds = queues.map(queue => ({
                id: queue.id,
                label: queue.id
            }));
            setIvrQueues(queueIds);
        }
        catch (error) {
            console.error(QUEUE_ERRORS.FETCH_QUEUE_ERROR, error);
        }
        finally {
            setLoadingQueues(false);
        }
    };

    const { skillsToDisplay, filteredSkillsLength } = useMemo(() => {
        const filteredSkills = userSkills.filter((userSkill) =>
            userSkill.skillname.trim().toLowerCase().includes(searchUserSkill.toLowerCase())
        );
        return {
            skillsToDisplay: filteredSkills
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredSkillsLength: filteredSkills.length,
        };
    }, [userSkills, searchUserSkill, order, orderBy, page, rowsPerPage]);

    const handleAddEditSkillClick = (skill = {}) => {
        setSelectedSkill(skill);
        setIsAddEditSkillModalOpen(true);
    };

    const handleSaveSkill = async () => {
        if (!selectedSkill) return;
        const skillToSave = {
            ...selectedSkill,
        };
        try {
            skillToSave.id ? await editUserSkill(skillToSave) : await addUserSkill(skillToSave);
            setIsAddEditSkillModalOpen(false);
            setSuccessMessage(skillToSave.id ? SKILL_MESSAGES.SKILL_UPDATED : SKILL_MESSAGES.SKILL_ADDED);
            await fetchSkills();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) { console.error(SKILL_ERRORS.SAVE_SKILL_ERROR, error); }
    };

    useEffect(() => {
        if (
            selectedSkill &&
            selectedSkill.queueIdreq === 0 &&
            selectedSkill.queueid !== null
        ) {
            setSelectedSkill((prev) => ({ ...prev, queueid: null }));
        }
    }, [selectedSkill?.queueIdreq]);

    const handleInputChange = (field) => (e) =>
        setSelectedSkill((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));


    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            {successMessage && 
                <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                    {successMessage}
                </Alert>
            }
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchUserSkill}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={SKILL_STRINGS.SEARCH_SKILLS}
                        onChange={e => setSearchUserSkill(e.target.value)}
                    />
                </Grid>
                <Grid size={SIZES.AUTO}>
                    <Button
                        startIcon={<Add />}
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                        onClick={() => handleAddEditSkillClick({})}
                    >
                        {SKILL_STRINGS.ADD_SKILL}
                    </Button>
                </Grid>
            </Grid>
            <SkillsTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                skills={skillsToDisplay}
                rowsPerPage={rowsPerPage}
                columns={SKILL_TABLE_COLUMNS}
                isSkillsLoading={loadingSkills || loadingQueues }
                setRowsPerPage={setRowsPerPage}
                onEdit={handleAddEditSkillClick}
                handleRequestSort={handleRequestSort}
                filteredSkillsLength={filteredSkillsLength}
            />
            <AddEditSkillDialog
                ivrQueues={ivrQueues}
                onSave={handleSaveSkill}
                selectedSkill={selectedSkill}
                open={isAddEditSkillModalOpen}
                handleInputChange={handleInputChange}
                onClose={() => setIsAddEditSkillModalOpen(false)}
            />
        </>
    );
};

export default Skills;